import { logger } from "../../../core";
import { GetPreviewRequest, GetPreviewResponse } from "../auto-discovery/types";
import datasourceApiManager from "../DatasourceApiService";
import {
  MappingWorkflowV2Output,
  UseCaseUserInfoRequest,
  UseCaseUserInfoResponse,
  WorkflowResumeRequest,
  WorkflowRunRequest,
  UseCaseRecommendationRequest,
  UseCaseRecommendationResponse,
  GetRuleInfoRequest,
  GetUserServiceMappingRulesResponses,
  PeopleBusinessInfoRequest,
  PeopleBusinessInfoResponse,
  GetEntityTypeInfoResponse
} from "./types";

class BicycleAiApiService {
  readonly bicycleAiV1Url = "/bicycle-ai/bicycle-ai/api/v1";

  async runWorkflow(payload: WorkflowRunRequest): Promise<MappingWorkflowV2Output> {
    try {
      const url = `${this.bicycleAiV1Url}/start_mapping_workflow_v2`;
      const response = await datasourceApiManager
        .getDefault()
        .post<MappingWorkflowV2Output, WorkflowRunRequest>(url, payload);
      return response.data;
    } catch (error) {
      logger.error("BicycleAiApiService", "Error while running workflow", error);
      throw error;
    }
  }

  async getUseCaseUserInfoResponse(payload: UseCaseUserInfoRequest): Promise<UseCaseUserInfoResponse> {
    try {
      const url = `${this.bicycleAiV1Url}/get_focus_group_role`;
      const response = await datasourceApiManager
        .getDefault()
        .post<UseCaseUserInfoResponse, UseCaseUserInfoRequest>(url, payload);
      return response.data;
    } catch (error) {
      logger.error("BicycleAiApiService", "Error while getting use case user info", error);
      throw error;
    }
  }

  async getWorkflowStatus(pollId: string): Promise<MappingWorkflowV2Output> {
    try {
      const url = `${this.bicycleAiV1Url}/status_mapping_workflow_v2?poll_id=${pollId}`;
      const response = await datasourceApiManager.getDefault().get<MappingWorkflowV2Output>(url);
      return response.data;
    } catch (error) {
      logger.error("BicycleAiApiService", "Error while getting workflow status", error);
      throw error;
    }
  }

  async resumeWorkflow(payload: WorkflowResumeRequest): Promise<MappingWorkflowV2Output> {
    try {
      const url = `${this.bicycleAiV1Url}/resume_mapping_workflow_v2`;
      const response = await datasourceApiManager
        .getDefault()
        .post<MappingWorkflowV2Output, WorkflowResumeRequest>(url, payload);
      return response.data;
    } catch (error) {
      logger.error("BicycleAiApiService", "Error while resuming workflow", error);
      throw error;
    }
  }

  async startUseCaseRecommendation(payload: UseCaseRecommendationRequest): Promise<UseCaseRecommendationResponse> {
    try {
      const url = `${this.bicycleAiV1Url}/use_case_recommendation_v2`;
      const response = await datasourceApiManager
        .getDefault()
        .post<UseCaseRecommendationResponse, UseCaseRecommendationRequest>(url, payload);
      return response.data;
    } catch (error) {
      logger.error("BicycleAiApiService", "Error while starting use case recommendation", error);
      throw error;
    }
  }

  async getUseCaseRecommendationStatus(pollId: string): Promise<UseCaseRecommendationResponse> {
    try {
      const url = `${this.bicycleAiV1Url}/use_case_recommendation_status_v2`;
      const response = await datasourceApiManager
        .getDefault()
        .post<UseCaseRecommendationResponse, { pollId: string }>(url, { pollId: pollId });
      return response.data;
    } catch (error) {
      logger.error("BicycleAiApiService", "Error while getting use case recommendation status", error);
      throw error;
    }
  }

  async getConnectionPreview(request: GetPreviewRequest): Promise<GetPreviewResponse> {
    try {
      const response = await datasourceApiManager
        .getDefault()
        .post<GetPreviewResponse, GetPreviewRequest>(`${this.bicycleAiV1Url}/get_preview`, request);
      return response.data;
    } catch (error) {
      logger.error("ConnectorApiService", "Error getting preview", error);
      throw error;
    }
  }

  async getRuleInfo(payload: GetRuleInfoRequest): Promise<GetUserServiceMappingRulesResponses> {
    try {
      const url = `${this.bicycleAiV1Url}/get_rules_info`;
      const response = await datasourceApiManager
        .getDefault()
        .post<GetUserServiceMappingRulesResponses, GetRuleInfoRequest>(url, payload);
      return response.data;
    } catch (error) {
      logger.error("BicycleAiApiService", "Error while getting rule info", error);
      throw error;
    }
  }

  async getEntityTypeInfo(payload: GetRuleInfoRequest): Promise<GetEntityTypeInfoResponse> {
    try {
      const url = `${this.bicycleAiV1Url}/get_rules_info`;
      const response = await datasourceApiManager
        .getDefault()
        .post<GetEntityTypeInfoResponse, GetRuleInfoRequest>(url, payload);
      return response.data;
    } catch (error) {
      logger.error("BicycleAiApiService", "Error while getting entity type info", error);
      throw error;
    }
  }

  async getPeopleBusinessInfo(payload: PeopleBusinessInfoRequest): Promise<PeopleBusinessInfoResponse> {
    try {
      const url = `${this.bicycleAiV1Url}/get_people_business_info`;
      const response = await datasourceApiManager
        .getDefault()
        .post<PeopleBusinessInfoResponse, PeopleBusinessInfoRequest>(url, payload);
      return response.data;
    } catch (error) {
      logger.error("BicycleAiApiService", "Error while getting people business info", error);
      throw error;
    }
  }
}

const bicycleAiApiService = new BicycleAiApiService();
export default bicycleAiApiService;
